// StockOutForm.jsx
import React, { useState, useEffect, useRef } from 'react';
import AsyncSelect from "react-select/async";
import { customFetch } from '../apiClient';
import BatchDropdown from '../components/BatchDropdown';

// ── React-Select styling (classNames untouched so keyboard nav keeps working) ──
const customStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '34px',
    fontSize: '12.5px',
    borderRadius: '7px',
    borderColor: state.isFocused ? '#dc2626' : '#cbd5e1',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(220,38,38,0.12)' : 'none',
    '&:hover': { borderColor: '#dc2626' },
    backgroundColor: 'white',
  }),
  valueContainer: (base) => ({ ...base, padding: '0 10px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: '32px' }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    backgroundColor: "white",
    color: "black",
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 10px 30px rgba(0,0,0,0.14)',
    fontSize: '12.5px',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (provided, state) => ({
    ...provided,
    padding: '8px 12px',
    backgroundColor: state.isSelected ? '#dc2626' : state.isFocused ? '#fef2f2' : 'white',
    color: state.isSelected ? '#fff' : '#1e293b',
    cursor: 'pointer',
  }),
  placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: '12.5px' }),
};

function initItem() {
  return {
    medID: null,
    medicineName: "",
    medicinecomposition: "",
    category: "",
    companyName: "",
    packSize: "",
    hsnCode: "",
    batchNo: "",
    exp: "",
    availableQty: 0,
    leaf: "",
    piece: "",
    qty: 0,
  };
}

// ── small display helpers (UI only) ──
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : "");
const isPastDate = (d) => {
  if (!d) return false;
  const dt = new Date(d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dt < today;
};

const StockOutForm = () => {
  const [type, setType] = useState("EXPIRED");

  const [batchOptions, setBatchOptions] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);

  const [currentItem, setCurrentItem] = useState(initItem());
  const [items, setItems] = useState([]);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const formRef = useRef(null);
  const addButtonRef = useRef(null);
  const medicineSelectRef = useRef(null);
  const batchDropdownRef = useRef(null);
  const leafInputRef = useRef(null);

  // ✅ Initial focus
  useEffect(() => {
    setTimeout(() => {
      const firstInput = formRef.current?.querySelector(
        'select, input:not([type="hidden"]), .react-select__input input'
      );
      if (firstInput) {
        firstInput.focus();
      }
    }, 300);
  }, []);

  // ✅ Auto-dismiss alerts (UI enhancement)
  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(""), 4000); return () => clearTimeout(t); }
  }, [success]);

  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(""), 4000); return () => clearTimeout(t); }
  }, [error]);

  // ✅ Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (formRef.current && e.target.closest("form") === formRef.current) {
        handleKeyDown(e);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // ✅ Medicine search
  const loadMedicineOptions = async (inputValue) => {
    if (!inputValue || inputValue.trim().length < 1) return [];

    try {
      const res = await customFetch(
        `/MedicineDT/search?medicine=${encodeURIComponent(inputValue.trim())}`
      );
      if (!res.ok) return [];
      const data = await res.json();

      return data.map((m) => ({
        value: m.medID,
        label: m.medicine,
        data: m,
      }));
    } catch {
      return [];
    }
  };

  const loadBatchNumbers = async (medID) => {
    if (!medID) return;

    setLoadingBatches(true);
    try {
      const res = await customFetch(`/MedicineBatch/batches/${medID}?hid=1&frid=1`);

      if (!res.ok) {
        setBatchOptions([]);
        return;
      }

      const data = await res.json();
      const options = data.map((batch) => ({
        value: batch.batchNo,
        label: `${batch.batchNo} | Avail: ${batch.balance}`,
        balance: batch.balance,
        exp: batch.expDate || "",
        mrp: batch.mrp,
        sRate: batch.sRate,
      }));

      setBatchOptions(options);
    } catch {
      setBatchOptions([]);
    } finally {
      setLoadingBatches(false);
    }
  };

  const handleMedicineSelect = async (selected) => {
    if (!selected) {
      setCurrentItem(initItem());
      setBatchOptions([]);
      return;
    }

    const m = selected.data;
    setCurrentItem({
      ...initItem(),
      medID: m.medID,
      medicineName: m.medicine,
      medicinecomposition: m.mediInUnit || "",
      category: m.categoryName || "",
      companyName: m.brandName || "",
      packSize: m.iUnit || "",
      hsnCode: m.hsncode || "",
    });

    await loadBatchNumbers(m.medID);

    setTimeout(() => {
      if (batchDropdownRef.current) {
        batchDropdownRef.current.focus();
      }
    }, 200);
  };

  const handleBatchSelectCustom = (batch) => {
    if (!batch) {
      setCurrentItem((prev) => ({
        ...prev,
        batchNo: "",
        availableQty: 0,
        exp: ""
      }));
      return;
    }

    setCurrentItem((prev) => ({
      ...prev,
      batchNo: batch.value,
      availableQty: batch.balance,
      exp: batch.exp || "",
    }));

    setTimeout(() => {
      if (leafInputRef.current) {
        leafInputRef.current.focus();
        leafInputRef.current.select();
      }
    }, 100);
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    const updatedItem = { ...currentItem, [name]: value };

    const packSize = parseFloat(updatedItem.packSize) || 1;
    const leaf = parseFloat(updatedItem.leaf) || 0;
    const piece = parseFloat(updatedItem.piece) || 0;
    updatedItem.qty = leaf * packSize + piece;

    setCurrentItem(updatedItem);
  };

  const handleAddItem = () => {
    if (!currentItem.medID) {
      setError("Please select a medicine.");
      return;
    }
    if (!currentItem.batchNo) {
      setError("Please select a batch number.");
      return;
    }
    if (Number(currentItem.qty) <= 0) {
      setError("Quantity must be greater than 0.");
      return;
    }
    if (Number(currentItem.qty) > Number(currentItem.availableQty)) {
      setError("Quantity cannot exceed available quantity.");
      return;
    }

    setError("");

    setItems((prev) => [{ ...currentItem }, ...prev]);
    setCurrentItem(initItem());
    setBatchOptions([]);

    setTimeout(() => {
      if (medicineSelectRef.current) {
        medicineSelectRef.current.focus();
      }
    }, 150);
  };

  const handleKeyDown = (e) => {
    const isInsideAsyncSelect =
      e.target.closest('.react-select__control') ||
      e.target.classList?.contains('react-select__input');

    if (isInsideAsyncSelect && e.key === 'Enter') {
      return;
    }

    const isReactSelectMenuOpen = document.querySelector('.react-select__menu');
    if (isReactSelectMenuOpen) return;

    const activeBatchDropdown = e.target.closest('[data-batch-dropdown="true"]');
    if (activeBatchDropdown) return;

    const batchPanelOpen = document.querySelector('.batch-dropdown-panel');
    if (batchPanelOpen) return;

    const focusableElements = formRef.current?.querySelectorAll(
      'input:not([disabled]):not([readonly]):not([type="hidden"]):not([tabindex="-1"]), ' +
      '.react-select__input input:not([disabled]):not([readonly]):not([type="hidden"]), ' +
      'select:not([disabled]):not([tabindex="-1"]), ' +
      'button:not([disabled]):not([tabindex="-1"])'
    );

    if (!focusableElements || focusableElements.length === 0) return;
    const currentIndex = Array.from(focusableElements).indexOf(e.target);

    const isAddButton =
      e.target.textContent?.includes("Add Item") ||
      e.target.classList?.contains('add-item-btn');

    if (e.key === "Tab") {
      return;
    }

    if (e.key === "Enter") {
      if (isAddButton) {
        e.preventDefault();
        handleAddItem();
        return;
      }

      if (currentIndex === focusableElements.length - 1) {
        e.preventDefault();
        e.target.closest("form").requestSubmit();
        return;
      }

      e.preventDefault();
      const nextIndex = Math.min(focusableElements.length - 1, currentIndex + 1);
      focusableElements[nextIndex]?.focus();
    }
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEditRowToForm = async (index) => {
    const row = items[index];
    if (!row) return;

    setCurrentItem(row);
    setItems((prev) => prev.filter((_, i) => i !== index));

    if (row.medID && row.batchNo) {
      await loadBatchNumbers(row.medID);

      setTimeout(() => {
        const batch = batchOptions.find(b => b.value === row.batchNo);
        if (batch) {
          setCurrentItem(prev => ({
            ...prev,
            batchNo: row.batchNo,
            availableQty: batch.balance || row.availableQty,
            exp: batch.exp || row.exp
          }));
        }
      }, 150);
    }

    setError("");
    setSuccess("");
  };

  // ✅ Save with updated payload
  const handleSave = async (e) => {
    e.preventDefault();

    if (items.length === 0) {
      setError("At least add one item.");
      return;
    }

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const payload = {
        type,
        items: items.map((it) => ({
          medID: it.medID,
          medicineName: it.medicineName,
          composition: it.medicinecomposition,
          brand: it.companyName,
          category: it.category,
          batchNo: it.batchNo,
          packSize: parseInt(it.packSize) || null,
          exp: it.exp || null,
          leaf: parseInt(it.leaf) || 0,
          piece: parseInt(it.piece) || 0,
          qty: it.qty,
          hsnCode: it.hsnCode || "",
        })),
      };

      const res = await customFetch('/StockOut/save', {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to save stock out.");
      }

      const data = await res.json();

      setSuccess(
        `Stock out saved! RecId: ${data.recId} | RecNo: ${data.recNo} | Type: ${data.type} | Items: ${data.itemCount} | Total Qty: ${data.totalQty} Pcs`
      );

      setItems([]);
      setCurrentItem(initItem());
      setType("EXPIRED");

      setTimeout(() => {
        const firstInput = formRef.current?.querySelector(
          'select, input:not([type="hidden"]), .react-select__input input'
        );
        if (firstInput) {
          firstInput.focus();
        }
      }, 300);

    } catch (err) {
      setError(err.message || "Error occurred");
    } finally {
      setLoading(false);
    }
  };

  const totalQty = items.reduce((sum, it) => sum + it.qty, 0);

  return (
    <div className="stockout-container">
      <div className="stockout-card">
        {/* Header */}
        <div className="stockout-header">
          <div className="header-left">
            <div className="header-icon"><i className="bi bi-box-arrow-up"></i></div>
            <div className="header-text">
              <h5>Stock Out Entry</h5>
              <p>Remove expired or damaged stock from inventory</p>
            </div>
          </div>
          <div className={`header-badge ${type === "EXPIRED" ? "badge-expired" : "badge-damaged"}`}>
            <i className={`bi ${type === "EXPIRED" ? "bi-calendar-x" : "bi-exclamation-triangle"}`}></i>
            <span>{type}</span>
          </div>
        </div>

        <div className="card-content">
          {error && (
            <div className="alert-custom alert-danger">
              <i className="bi bi-exclamation-circle-fill"></i> {error}
            </div>
          )}
          {success && (
            <div className="alert-custom alert-success">
              <i className="bi bi-check-circle-fill"></i> {success}
            </div>
          )}

          <form ref={formRef} onSubmit={handleSave}>
            {/* Type Selection — segmented pills */}
            <div className="type-row">
              <label className="type-label">Stock Out Type <span className="req">*</span></label>
              <div className="pill-group">
                <button
                  type="button"
                  tabIndex="-1"
                  className={`pill pill-expired ${type === "EXPIRED" ? "active" : ""}`}
                  onClick={() => setType("EXPIRED")}
                >
                  <i className="bi bi-calendar-x"></i> Expired
                </button>
                <button
                  type="button"
                  tabIndex="-1"
                  className={`pill pill-damaged ${type === "DAMAGED" ? "active" : ""}`}
                  onClick={() => setType("DAMAGED")}
                >
                  <i className="bi bi-exclamation-triangle"></i> Damaged
                </button>
              </div>
            </div>

            {/* Entry Section */}
            <div className="entry-section">
              <div className="section-title">
                <i className="bi bi-plus-circle"></i><span>Add Item</span>
              </div>
              <div className="section-body">

                {/* Medicine + details */}
                <div className="entry-grid">
                  <div className="form-group span-2">
                    <label className="form-label">
                      <i className="bi bi-capsule"></i> Search Medicine <span className="req">*</span>
                    </label>
                    <div onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const menu = document.querySelector('.react-select__menu');
                        if (menu) {
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }
                        if (!currentItem.medID && items.length > 0) return;
                        if (currentItem.medID) return;
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}>
                      <AsyncSelect
                        ref={medicineSelectRef}
                        cacheOptions
                        loadOptions={loadMedicineOptions}
                        onChange={handleMedicineSelect}
                        placeholder="Start typing medicine name..."
                        isClearable
                        styles={customStyles}
                        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                        menuPosition="fixed"
                        value={
                          currentItem.medID
                            ? { value: currentItem.medID, label: currentItem.medicineName }
                            : null
                        }
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Composition</label>
                    <input className="form-input ro" value={currentItem.medicinecomposition} readOnly placeholder="—" />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <input className="form-input ro" value={currentItem.category} readOnly placeholder="—" />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Company</label>
                    <input className="form-input ro" value={currentItem.companyName} readOnly placeholder="—" />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Pack Size</label>
                    <input className="form-input ro" value={currentItem.packSize} readOnly placeholder="—" />
                  </div>

                  <div className="form-group">
                    <label className="form-label">HSN Code</label>
                    <input className="form-input ro" value={currentItem.hsnCode} readOnly placeholder="—" />
                  </div>
                </div>

                {/* Batch row */}
                {currentItem.medID && (
                  <div className="batch-grid">
                    <div className="form-group">
                      <label className="form-label">
                        <i className="bi bi-upc-scan"></i> Batch No <span className="req">*</span>
                      </label>
                      <BatchDropdown
                        ref={batchDropdownRef}
                        options={batchOptions}
                        value={currentItem.batchNo}
                        onChange={handleBatchSelectCustom}
                        loading={loadingBatches}
                        disabled={batchOptions.length === 0}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Expiry Date</label>
                      <div className={`info-field ${currentItem.exp && isPastDate(currentItem.exp) ? "info-expired" : "info-amber"}`}>
                        <i className="bi bi-calendar-event"></i>
                        <span>{currentItem.exp ? fmtDate(currentItem.exp) : "—"}</span>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Available Qty</label>
                      <div className="info-field info-stock">
                        <i className="bi bi-box-seam"></i>
                        <span>{currentItem.availableQty} <small>Pcs</small></span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Quantity row */}
                {currentItem.batchNo && (
                  <div className="qty-grid">
                    <div className="form-group">
                      <label className="form-label">Leaf <span className="req">*</span></label>
                      <input
                        ref={leafInputRef}
                        className="form-input"
                        name="leaf"
                        inputMode="decimal"
                        value={currentItem.leaf}
                        onChange={handleItemChange}
                        placeholder="0"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Piece <span className="req">*</span></label>
                      <input
                        className="form-input"
                        name="piece"
                        inputMode="decimal"
                        value={currentItem.piece}
                        onChange={handleItemChange}
                        placeholder="0"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Total Qty (Pcs)</label>
                      <div className={`total-field ${Number(currentItem.qty) > Number(currentItem.availableQty) ? "total-over" : ""}`}>
                        <i className="bi bi-calculator"></i>
                        <span>{currentItem.qty}</span>
                        {Number(currentItem.qty) > Number(currentItem.availableQty) && (
                          <small className="over-tag">exceeds stock</small>
                        )}
                      </div>
                    </div>

                    <div className="form-group add-group">
                      <label className="form-label">&nbsp;</label>
                      <button
                        ref={addButtonRef}
                        type="button"
                        className="btn-add add-item-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAddItem();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            handleAddItem();
                          }
                        }}
                      >
                        <i className="bi bi-plus-lg"></i> Add Item
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Items Table */}
            <div className="list-section">
              <div className="section-title">
                <i className="bi bi-list-check"></i><span>Items Added ({items.length})</span>
              </div>
              <div className="section-body p-0">
                <div className="table-responsive">
                  <table className="custom-table">
                    <thead>
                      <tr>
                        <th className="col-sr">#</th>
                        <th>Medicine</th>
                        <th>Category</th>
                        <th>Company</th>
                        <th>Batch</th>
                        <th>Expiry</th>
                        <th className="num">Leaf</th>
                        <th className="num">Piece</th>
                        <th className="num">Qty (Pcs)</th>
                        <th>HSN</th>
                        <th className="col-actions">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan="11" className="empty-state">
                            <div className="empty-inner">
                              <i className="bi bi-inbox"></i>
                              <span>No items added yet. Search a medicine to begin.</span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        items.map((it, idx) => (
                          <tr key={idx}>
                            <td className="col-sr text-muted">{idx + 1}</td>
                            <td className="text-start fw-600">{it.medicineName}</td>
                            <td>{it.category || "—"}</td>
                            <td>{it.companyName || "—"}</td>
                            <td><span className="batch-chip">{it.batchNo}</span></td>
                            <td>
                              <span className={`date-chip ${isPastDate(it.exp) ? "date-expired" : ""}`}>
                                {it.exp ? fmtDate(it.exp) : "—"}
                              </span>
                            </td>
                            <td className="num">{it.leaf}</td>
                            <td className="num">{it.piece}</td>
                            <td className="num fw-700 qty-cell">{it.qty}</td>
                            <td>{it.hsnCode || "—"}</td>
                            <td className="col-actions">
                              <div className="btn-group-actions">
                                <button
                                  type="button"
                                  className="btn-icon btn-edit"
                                  onClick={() => handleEditRowToForm(idx)}
                                  title="Edit"
                                  tabIndex="-1"
                                >
                                  <i className="bi bi-pencil-square"></i>
                                </button>
                                <button
                                  type="button"
                                  className="btn-icon btn-delete"
                                  onClick={() => handleRemoveItem(idx)}
                                  title="Delete"
                                  tabIndex="-1"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Footer: summary + save */}
            <div className="form-footer">
              <div className="summary-chips">
                <div className="chip">
                  <span className="chip-label">Total Items</span>
                  <span className="chip-value">{items.length}</span>
                </div>
                <div className="chip chip-accent">
                  <span className="chip-label">Total Quantity</span>
                  <span className="chip-value">{totalQty} <small>Pcs</small></span>
                </div>
              </div>

              <button
                type="submit"
                className="btn-save"
                disabled={loading || items.length === 0}
              >
                {loading
                  ? <><span className="spinner"></span> Saving...</>
                  : <><i className="bi bi-check-lg"></i> Save Stock Out</>}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* STYLES */}
      <style jsx>{`
        .stockout-container {
          --so-accent: #dc2626;
          --so-accent-dark: #b91c1c;
          --so-amber: #d97706;
          padding: 0;
          font-family: system-ui, -apple-system, sans-serif;
        }
        .stockout-card {
          background: #fff;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 6px rgba(0,0,0,0.08);
          overflow: hidden;
        }

        /* Header */
        .stockout-header {
          background: linear-gradient(135deg, var(--so-accent), var(--so-accent-dark));
          padding: 12px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
          border-bottom: 3px solid #fca5a5;
        }
        .header-left { display: flex; align-items: center; gap: 12px; color: #fff; }
        .header-icon {
          width: 34px; height: 34px; border-radius: 8px;
          background: rgba(255,255,255,0.18);
          display: flex; align-items: center; justify-content: center;
          font-size: 17px;
        }
        .header-text h5 { margin: 0; font-size: 15px; font-weight: 700; letter-spacing: .2px; }
        .header-text p { margin: 0; font-size: 11px; color: rgba(255,255,255,0.85); }
        .header-badge {
          display: flex; align-items: center; gap: 6px;
          padding: 5px 12px; border-radius: 30px;
          font-size: 11px; font-weight: 700; letter-spacing: .4px;
          background: rgba(255,255,255,0.92);
        }
        .badge-expired { color: var(--so-amber); }
        .badge-damaged { color: var(--so-accent-dark); }

        .card-content { padding: 14px 16px; }

        /* Alerts */
        .alert-custom {
          margin-bottom: 12px; padding: 8px 12px; border-radius: 6px;
          font-size: 12px; font-weight: 500;
          display: flex; align-items: center; gap: 8px;
          animation: fadeIn .2s ease;
        }
        .alert-danger { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
        .alert-success { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }

        /* Type pills */
        .type-row {
          display: flex; align-items: center; gap: 14px;
          background: #f8fafc; border: 1px solid #e2e8f0;
          border-radius: 8px; padding: 10px 14px; margin-bottom: 14px;
          flex-wrap: wrap;
        }
        .type-label { font-size: 12px; font-weight: 600; color: #475569; margin: 0; }
        .req { color: var(--so-accent); }
        .pill-group { display: flex; gap: 8px; }
        .pill {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 7px 16px; border-radius: 30px; cursor: pointer;
          font-size: 12px; font-weight: 600; transition: .15s;
          border: 1.5px solid #cbd5e1; background: #fff; color: #64748b;
        }
        .pill:hover { border-color: #94a3b8; }
        .pill-expired.active {
          background: var(--so-amber); border-color: var(--so-amber); color: #fff;
          box-shadow: 0 2px 8px rgba(217,119,6,0.3);
        }
        .pill-damaged.active {
          background: var(--so-accent); border-color: var(--so-accent); color: #fff;
          box-shadow: 0 2px 8px rgba(220,38,38,0.3);
        }

        /* Sections */
        .entry-section, .list-section {
          border: 1px solid #e2e8f0; border-radius: 8px;
          overflow: hidden; margin-bottom: 14px;
        }
        .section-title {
          background: #334155; color: #fff;
          padding: 7px 12px; font-size: 12px; font-weight: 600;
          display: flex; align-items: center; gap: 7px;
        }
        .section-body { padding: 12px; background: #fff; }
        .p-0 { padding: 0; }

        /* Grids */
        .entry-grid {
          display: grid; gap: 10px; margin-bottom: 10px;
          grid-template-columns: repeat(7, 1fr);
        }
        .entry-grid .span-2 { grid-column: span 2; }
        .batch-grid {
          display: grid; gap: 10px; margin-bottom: 10px;
          grid-template-columns: repeat(3, minmax(0, 220px));
        }
        .qty-grid {
          display: grid; gap: 10px;
          grid-template-columns: 110px 110px 180px 1fr;
          align-items: end;
        }

        .form-group { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
        .form-label {
          font-size: 11px; font-weight: 600; color: #475569;
          display: flex; align-items: center; gap: 5px; white-space: nowrap;
        }
        .form-label i { color: var(--so-accent); }

        .form-input {
          height: 34px; padding: 6px 9px;
          border: 1px solid #cbd5e1; border-radius: 7px;
          font-size: 12.5px; color: #1e293b; background: #fff;
          outline: none; transition: .15s; width: 100%;
        }
        .form-input:focus {
          border-color: var(--so-accent);
          box-shadow: 0 0 0 3px rgba(220,38,38,0.12);
        }
        .form-input.ro {
          background: #f1f5f9; color: #64748b; cursor: default;
          border-color: #e2e8f0;
        }

        /* Info fields (expiry / available) */
        .info-field {
          height: 34px; border-radius: 7px; padding: 0 10px;
          display: flex; align-items: center; gap: 7px;
          font-size: 12.5px; font-weight: 600;
        }
        .info-field small { font-weight: 500; opacity: .7; }
        .info-amber { background: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
        .info-expired { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
        .info-stock { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }

        /* Total field */
        .total-field {
          height: 34px; border-radius: 7px; padding: 0 12px;
          display: flex; align-items: center; gap: 8px;
          background: #16a34a; color: #fff;
          font-size: 14px; font-weight: 700;
          box-shadow: 0 2px 6px rgba(22,163,74,0.25);
        }
        .total-field.total-over { background: var(--so-accent); box-shadow: 0 2px 6px rgba(220,38,38,0.25); }
        .over-tag { font-size: 9px; font-weight: 600; opacity: .9; margin-left: auto; }

        /* Add button */
        .add-group { align-self: end; }
        .btn-add {
          height: 34px; padding: 0 18px; border: none; border-radius: 7px;
          background: var(--so-accent); color: #fff;
          font-size: 12px; font-weight: 700; cursor: pointer;
          display: inline-flex; align-items: center; justify-content: center; gap: 6px;
          transition: .15s; width: 100%;
        }
        .btn-add:hover { background: var(--so-accent-dark); }

        /* Table */
        .table-responsive { overflow-x: auto; }
        .custom-table { width: 100%; border-collapse: collapse; font-size: 11.5px; }
        .custom-table thead th {
          background: #f1f5f9; color: #475569;
          padding: 8px 10px; font-weight: 600; text-align: center;
          border-bottom: 1px solid #cbd5e1; white-space: nowrap;
          text-transform: uppercase; font-size: 10px; letter-spacing: .3px;
        }
        .custom-table td {
          padding: 7px 10px; border-bottom: 1px solid #eef2f7;
          color: #334155; text-align: center; vertical-align: middle;
        }
        .custom-table tbody tr:hover { background: #fafbfc; }
        .text-start { text-align: left; }
        .num { text-align: center; }
        .text-muted { color: #94a3b8; }
        .fw-600 { font-weight: 600; }
        .fw-700 { font-weight: 700; }
        .col-sr { width: 40px; }
        .col-actions { width: 90px; }
        .qty-cell { color: #16a34a; }

        .batch-chip {
          background: #eef2ff; color: #4338ca; border: 1px solid #c7d2fe;
          padding: 2px 8px; border-radius: 5px; font-weight: 600; font-size: 11px;
        }
        .date-chip {
          background: #fffbeb; color: #b45309; border: 1px solid #fde68a;
          padding: 2px 8px; border-radius: 5px; font-weight: 600; font-size: 11px;
        }
        .date-chip.date-expired { background: #fef2f2; color: #dc2626; border-color: #fecaca; }

        .empty-state {
          padding: 28px 12px !important; color: #94a3b8; text-align: center;
        }
        .empty-inner {
          display: flex; flex-direction: column; align-items: center; gap: 8px;
        }
        .empty-inner i { font-size: 26px; opacity: .6; }
        .empty-inner span { font-size: 12px; font-style: italic; }

        .btn-group-actions { display: flex; justify-content: center; gap: 5px; }
        .btn-icon {
          width: 26px; height: 26px; border: none; border-radius: 5px;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer; font-size: 12px; transition: .15s;
        }
        .btn-edit { background: #eff6ff; color: #2563eb; }
        .btn-edit:hover { background: #dbeafe; }
        .btn-delete { background: #fef2f2; color: #dc2626; }
        .btn-delete:hover { background: #fee2e2; }

        /* Footer */
        .form-footer {
          display: flex; justify-content: space-between; align-items: center;
          flex-wrap: wrap; gap: 12px;
          padding-top: 12px; border-top: 1px solid #e2e8f0;
        }
        .summary-chips { display: flex; gap: 10px; flex-wrap: wrap; }
        .chip {
          display: flex; flex-direction: column; gap: 2px;
          background: #f8fafc; border: 1px solid #e2e8f0;
          border-radius: 8px; padding: 6px 16px; min-width: 110px;
        }
        .chip-label { font-size: 10px; color: #64748b; font-weight: 500; text-transform: uppercase; letter-spacing: .3px; }
        .chip-value { font-size: 16px; font-weight: 700; color: #1e293b; }
        .chip-value small { font-size: 11px; font-weight: 500; color: #64748b; }
        .chip-accent { background: #f0fdf4; border-color: #bbf7d0; }
        .chip-accent .chip-value { color: #16a34a; }

        .btn-save {
          padding: 0 28px; height: 40px; border: none; border-radius: 8px;
          background: var(--so-accent); color: #fff;
          font-size: 13px; font-weight: 700; cursor: pointer;
          display: inline-flex; align-items: center; gap: 8px; transition: .15s;
          box-shadow: 0 2px 8px rgba(220,38,38,0.3);
        }
        .btn-save:hover:not(:disabled) { background: var(--so-accent-dark); }
        .btn-save:disabled { opacity: .5; cursor: not-allowed; box-shadow: none; }

        .spinner {
          width: 14px; height: 14px; border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff;
          animation: spin .7s linear infinite;
        }

        @keyframes fadeIn { from { opacity: 0; transform: translateY(-3px); } to { opacity: 1; transform: none; } }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* Responsive */
        @media (max-width: 1100px) {
          .entry-grid { grid-template-columns: repeat(4, 1fr); }
          .entry-grid .span-2 { grid-column: span 2; }
        }
        @media (max-width: 768px) {
          .entry-grid { grid-template-columns: repeat(2, 1fr); }
          .batch-grid { grid-template-columns: 1fr 1fr; }
          .qty-grid { grid-template-columns: 1fr 1fr; }
          .add-group { grid-column: span 2; }
          .form-footer { flex-direction: column; align-items: stretch; }
          .btn-save { justify-content: center; }
        }
        @media (max-width: 480px) {
          .entry-grid, .entry-grid .span-2 { grid-template-columns: 1fr; grid-column: auto; }
          .batch-grid, .qty-grid { grid-template-columns: 1fr; }
          .add-group { grid-column: auto; }
        }
      `}</style>
    </div>
  );
};

export default StockOutForm;